import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { RequestContext } from "../../context/request.context";
import { MdAddLink, MdDelete } from "react-icons/md";
import { FiEdit, FiImage } from "react-icons/fi";
import { BiSave, BiUpload } from "react-icons/bi";
import PageHeader from "../../components/ui/PageHeader";

const Banner = ({ data, editBanner, deleteBanner }) => {
  const [imageSrc, setImageSrc] = useState(data.photo?.original?.url);
  const [isChanged, setIsChanged] = useState(false);
  const { request } = useContext(RequestContext);
  const [file, setFile] = useState(null);
  const uploadFile = async () => {
    const formData = new FormData();
    formData.append("file", file);
    await request(
      `/banner/${data.id}/photo`,
      "POST",
      formData,
      "multipart/form-data"
    );
    setIsChanged(false);
  };
  return (
    <Card sx={{ maxWidth: 400, borderRadius: "10px" }}>
      <Box
        sx={{
          position: "relative",
        }}
      >
        <CardMedia
          sx={{
            height: 300,
            objectFit: "cover",
            width: "100%",
            minWidth: "300px",
          }}
          image={imageSrc}
          title={data.description}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "2px 0",
            position: "absolute",
            right: 0,
            top: 0,
            zIndex: 1,
            bgcolor: "background.paper",
            backdropFilter: "blur(10px)",
            borderRadius: "0 0 0 5px",
            border: 1,
            borderColor: "divider",
          }}
        >
          <IconButton size="small" color="primary">
            <BiUpload />
            <input
              accept="image/*"
              type="file"
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: "pointer",
              }}
              onChange={(e) => {
                const file = e.target.files[0];
                setFile(file);
                const reader = new FileReader();
                reader.onload = (e) => {
                  setImageSrc(e.target.result);
                };
                reader.readAsDataURL(file);
                setIsChanged(true);
              }}
            />
          </IconButton>
          {isChanged && (
            <IconButton size="small" color="success" onClick={async () => await uploadFile()}>
              <BiSave />
            </IconButton>
          )}
          <IconButton
            size="small"
            color="warning"
            onClick={async () => {
              await editBanner(data);
            }}
          >
            <FiEdit />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={async () => {
              await deleteBanner(data);
            }}
          >
            <MdDelete />
          </IconButton>
        </Box>
      </Box>
      <CardContent>
        <Typography gutterBottom variant="h5" component="div">
          {data.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {data.description}
        </Typography>
      </CardContent>
    </Card>
  );
};

const AddBanner = ({ data, action }) => {
  const [withLink, setWithLink] = useState(false);

  return (
    <Box
      sx={{
        padding: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const json = Object.fromEntries(formData);
        json.type = withLink ? "PRODUCTS" : "INFORMATION";
        action(json);
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Banner Yaratish</Typography>
        <IconButton
          size="small"
          color={withLink ? "primary" : "default"}
          onClick={() => {
            setWithLink(!withLink);
          }}
          title={withLink ? "Linkni o'chirish" : "Linkni qo'shish"}
        >
          <MdAddLink />
        </IconButton>
      </Box>
      <TextField
        fullWidth
        label="Banner nomi"
        variant="outlined"
        name="title"
        size="small"
        defaultValue={data?.title}
      />
      <TextField
        fullWidth
        label="Banner haqida ma'lumot"
        variant="outlined"
        name="description"
        multiline
        size="small"
        rows={3}
        defaultValue={data?.description}
      />
      {withLink && (
        <TextField
          fullWidth
          label="Bannerga link"
          variant="outlined"
          name="url"
          type="url"
          size="small"
          defaultValue={data?.url}
        />
      )}
      <Button variant="contained" color="primary" type="submit">
        Saqlash
      </Button>
    </Box>
  );
};

const DeleteDialog = ({ onDelete, onClose }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        p: 2,
      }}
    >
      <Typography variant="h6" textAlign={"center"}>
        Ishonchingiz Komilmi?
      </Typography>
      <Typography variant="body2" textAlign={"center"}>
        Siz ushbu bannerni o'chirmoqdasiz va uni qayta tiklab bolmaydi
      </Typography>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}
      >
        <Button variant="outlined" color="error" onClick={onDelete} fullWidth>
          Ha
        </Button>
        <Button variant="contained" color="primary" onClick={onClose} fullWidth>
          Yo'q
        </Button>
      </Box>
    </Box>
  );
};

const Banners = () => {
  const { request } = useContext(RequestContext);
  const [rows, setRows] = useState([]);
  // const [loading, setLoading] = useState(false);
  useEffect(() => {
    // setLoading(true);
    request(`/banner/all`, "GET").then((res) => {
      // setLoading(false);
      setRows(res);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addNewBanner = async (data) => {
    const res = await request("/banner/save", "POST", data);
    setRows([res, ...rows]);
    handleClose();
  };

  const [dialog, setDialog] = useState({
    open: false,
    component: null,
  });

  const handleClose = () => {
    setDialog({
      open: false,
      component: null,
    });
  };

  const editBanner = async (id, json) => {
    const data = await request(`/banner/${id}`, "PUT", json);
    const newRows = rows.map((row) => {
      if (row.id === data.id) {
        return data;
      }
      return row;
    });
    setRows(newRows);
    handleClose();
  };

  const deleteBanner = async (id) => {
    await request(`/banner/${id}`, "DELETE");
    const newRows = rows.filter((row) => row.id !== id);
    setRows(newRows);
    handleClose();
  };

  return (
    <Box>
      <PageHeader
        title="Bannerlar"
        subtitle={
          rows.length
            ? `${rows.length} ta banner. Ular ilovaning bosh sahifasida ko'rinadi.`
            : "Ilovaning bosh sahifasida ko'rinadigan rasmlar."
        }
      />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            width: "400px",
            minHeight: "300px",
            // Ranglar mavzudan olinadi -- ilgari bu yerda oq fon va kulrang
            // chegara qattiq yozilgan edi va qorong'i ko'rinishda matn
            // umuman o'qilmasdi.
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 3,
            cursor: "pointer",
            bgcolor: "background.paper",
            color: "text.secondary",
            transition: "border-color .15s, background-color .15s",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "action.hover",
            },
          }}
          role="button"
          onClick={() => {
            setDialog({
              open: true,
              component: <AddBanner action={addNewBanner} />,
            });
          }}
        >
          <Box sx={{ fontSize: 26, mb: 1, display: "flex", color: "text.secondary" }}>
            <FiImage />
          </Box>
          <Typography variant="body1">Yangi banner qo'shish</Typography>
          <Typography variant="caption">
            Yangi banner qo'shish uchun ustiga bosing
          </Typography>
        </Box>
        {rows.map((row) => (
          <Banner
            data={row}
            key={row.id}
            editBanner={(data) => {
              setDialog({
                open: true,
                component: (
                  <AddBanner
                    action={async (json) => {
                      await editBanner(row.id, json);
                    }}
                    data={data}
                  />
                ),
              });
            }}
            deleteBanner={async () => {
              setDialog({
                open: true,
                component: (
                  <DeleteDialog
                    onClose={handleClose}
                    onDelete={async () => {
                      await deleteBanner(row.id);
                    }}
                  />
                ),
              });
            }}
          />
        ))}
      </Box>
      <Dialog
        open={dialog.open}
        onClose={() => {
          setDialog({
            open: false,
            component: null,
          });
        }}
        fullWidth
        maxWidth="md"
      >
        {dialog.component}
      </Dialog>
    </Box>
  );
};

export default Banners;
